import React, { useState, useEffect } from 'react';
import TukanomaImg from '../../assets/images/tukanoma.webp';
import MidigeneImg from '../../assets/images/midigene.webp';
import FlowIntervalImg from '../../assets/images/flowinterval.webp';
import EitanImg from '../../assets/images/eitan.webp';
import IMessageImg from '../../assets/images/imessage.webp';
import LineImg from '../../assets/images/line.webp';
import PortfolioImg from '../../assets/images/lp.jpg';

const Portfolio: React.FC = () => {
  const [activeApp, setActiveApp] = useState<any>(null);
  const [isVisible, setIsVisible] = useState(false);

  const openModal = (app: any) => {
    setActiveApp(app);
    // 次のフレームで表示フラグを立てることでアニメーションを発火
    requestAnimationFrame(() => {
      setIsVisible(true);
    });
  };

  const closeModal = () => {
    setIsVisible(false);
    // アニメーションが終わるのを待ってからデータをクリア（500ms）
    setTimeout(() => {
      setActiveApp(null);
    }, 500);
  };

  // Escキーでも閉じれるようにしてあげるわ（優しさよ！）
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeModal();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, []);

  const applicationApps = [
    {
      id: 1,
      platform: 'iOS',
      title: 'TukanomaCamera',
      tagline: 'Syncing Vision with Rhythm.',
      description:
        '1.0秒や2.0秒といった「指定した秒数」で正確に録画を終了し、自動保存するクリエイター向けカメラアプリ。',
      overview:
        '選択した特定の秒数が「どのBPMの何拍分に相当するか」をリアルタイムに算出・表示する逆引き機能です。撮影した素材をDTMや動画編集ソフトに取り込んだ際、プロジェクトのBPMに完璧に同期させることができます。編集時の微調整という「手間」を、撮影の瞬間に解消するワークフローを提供します。',
      category: 'Utility',
      image: TukanomaImg,
      technologies: ['Swift', 'SwiftUI'],
      highlights: [
        {
          title: '高精度な固定長レコーディング & 自動トリミング',
          content:
            'AVFoundationのAVCaptureMovieFileOutputを制御し、指定秒数で録画を停止。さらに、録画終了後にAVAssetExportSessionを用いて、カメラの起動遅延や処理の揺らぎを排除した「純粋な指定秒数」の動画ファイルをミリ秒単位で生成・保存するロジックを実装。',
        },
        {
          title: '秒数からBPMを導き出す逆引きアルゴリズム',
          content:
            '独自のBPMCalculatorクラスを実装。ユーザーが選択した秒数に対し、音楽的に整合性の取れる整数BPMと音符（4分音符、2分音符等）の組み合わせを即座にマッピングしてUIにフィードバック。',
        },
        {
          title: 'Swift Concurrencyによるストレスフリーな保存処理',
          content:
            '動画のトリミングから、プレビュー用のサムネイル生成、PhotoKitを用いたカスタムアルバムへの保存までの一連の重い処理を非同期で実行。録画直後でも次の撮影にすぐ移れる、軽快な操作性を確保。',
        },
        {
          title: 'クリエイターに最適化されたUX設計',
          content:
            'ボリュームボタンを物理シャッターとして利用可能にしつつ、ボタン押下時の物理的な音や振動が録画の冒頭に入らないよう、精密な録画開始ディレイ（0.15秒）をプログラム側で制御。現場での実用性を追求。',
        },
        {
          title: 'マニュアル制御とプレミアム機能管理',
          content:
            'ISOやフォーカスのマニュアル操作、4K撮影（プレミアム機能）の切り替えなど、プロユースに耐える機能を搭載。StoreKitを用いたサブスクリプション/買い切りモデルの収益化基盤も実装。',
        },
      ],
      links: [
        {
          label: 'View on App Store',
          url: 'https://apps.apple.com/app/id6756915000',
        },
      ],
    },
    {
      id: 2,
      platform: 'iOS',
      title: 'IkatomapeMidigene',
      tagline: 'Endless MIDI. Infinite Ideas.',
      description:
        '音楽理論アルゴリズムを搭載した、iOS/macOS対応の楽曲スケッチ＆MIDI生成ツール。',
      overview:
        '「作曲の知識がなくても1秒でプロ品質のフレーズを」というコンセプトのもと、厳選された50種類以上のコード進行と20種類以上の音楽理論アルゴリズムを組み合わせ、メロディ・コード・ベース・ドラムを一瞬で生成します。',
      category: 'Music',
      image: MidigeneImg,
      technologies: [
        'Swift',
        'SwiftUI',
        'AVFoundation',
        'AVAudioEngine',
        'SMF Binary Writer',
      ],
      highlights: [
        {
          title: 'フル・ネイティブ設計 (Pure Swift & AVFoundation)',
          content:
            'AudioKit等の外部ライブラリに一切依存せず、Apple標準のAVFoundation（AVAudioEngine, AVAudioUnitSampler）を極限まで使い倒した、軽量かつ極めて安定した音楽エンジンを構築。',
        },
        {
          title: '独自MIDIバイナリ・ライターの実装',
          content:
            'CoreMIDIに依存せず、Standard MIDI File (SMF) のバイナリ構造をSwiftでゼロから組み立てるカスタムエンジンを開発。これにより、ステム書き出しやトラック名埋め込みなど、DAWでの制作に最適化された高精度なMIDI出力を実現。',
        },
        {
          title: '音楽理論アルゴリズムの実装',
          content:
            'ボイスリーディング（声部連結の移動距離最小化計算）、代理コード自動選出、アボイドノート判定、度数に基づいた戦略的テンション付与など、20種類以上の理論ロジックをコード化。',
        },
        {
          title: 'マルチプラットフォーム展開 (Universal Purchase)',
          content:
            'SwiftUIを活用し、iPhone/iPad/Mac 各デバイスに最適化したUIを1つのコードベースで実現。Mac版ではメニューバーや自由なリサイズに対応したネイティブなUX提供。',
        },
      ],
      links: [
        {
          label: 'iOS TestFlight',
          url: 'https://testflight.apple.com/join/UVTz2UtR',
        },
        {
          label: 'macOS TestFlight',
          url: 'https://testflight.apple.com/join/f3nmMsDU',
        },
      ],
    },
    {
      id: 3,
      platform: 'iOS',
      title: 'FlowInterval',
      tagline: 'Master Your Flow.',
      description:
        '複数の「工程（ステップ）」を自由に組み合わせて一つの「プリセット」として管理できるインターバルタイマーアプリ。',
      overview:
        'トレーニング、勉強、料理、あるいはビジネスにおけるプレゼン練習など、特定のタイムスケジュールに沿って動く必要があるユーザーのために設計しました。直感的な円形プログレスUIと、次に何をすべきかが一目でわかるステップリストにより、ユーザーがスマートフォンに触れることなく、自分のリズムを維持できる体験を提供します。',
      category: 'Productivity',
      image: FlowIntervalImg,
      technologies: [
        'Swift',
        'SwiftUI',
        'SwiftData',
        'Combine',
        'UserNotifications',
      ],
      highlights: [
        {
          title: 'バックグラウンド同期と整合性の担保',
          content:
            'タイマー実行中にアプリがバックグラウンドに移行しても、scenePhaseの変化を検知し、復帰時にDateベースで経過時間を再計算するロジックを実装。iOSのシステム制限下でも、秒単位で正確なタイマーの進捗とUIの同期を実現。',
        },
        {
          title: '高度なローカル通知スケジューリング',
          content:
            'タイマー開始時に全ステップの終了予定時刻を計算し、UNUserNotificationCenterを用いて通知を一括スケジュール。これにより、アプリが完全にキルされた状態でも、各ステップの切り替わりをユーザーに知らせることが可能。',
        },
        {
          title: 'SwiftDataによるリレーショナル・データ管理',
          content:
            '最新のSwiftDataを採用し、Preset（親）とTimerStep（子）の一対多のリレーションを管理。カスケード削除や並び替え順序の永続化など、複雑なデータ操作をリアクティブかつ安全に処理。',
        },
        {
          title: 'レスポンシブなUI/UX設計',
          content:
            'GeometryReaderを活用し、iPhoneの縦・横画面、iPadのマルチタスク環境にも適応するアダプティブレイアウトを実現。また、触覚フィードバックやカウントダウン音など、感覚的なユーザー体験の向上を追求。',
        },
        {
          title: '独自のデザインシステム構築',
          content:
            'Color・Fontの拡張を行い、一貫性のあるデザインシステムを構築。テーマ変更や将来的な機能拡張にも柔軟に対応できるメンテナブルなコードベースを実現。',
        },
      ],
      links: [
        {
          label: 'View on App Store',
          url: 'https://apps.apple.com/app/id6757172069',
        },
      ],
    },
    {
      id: 4,
      platform: 'iOS',
      title: '英たん (Etan)',
      tagline: '15 Minutes to Mastery.',
      description:
        '「1日15分で成果を出す」をコンセプトにした、超効率重視の英語学習アプリケーション。',
      overview:
        '「1日15分で成果を出す」をコンセプトにした、超効率重視の英語学習アプリケーションです。単なる単語帳ではなく、ユーザーの学習心理を分析したインターフェースが特徴です。レベル別・目的別（TOEIC、大学受験等）に細分化された30語ずつの「セクション学習」により、隙間時間でのマイクロラーニングを促進します。独自の「オートプレイ・リスニングモード」と「4択クイズモード」を搭載し、インプットからアウトプットまでをシームレスに完結。さらに、学習の継続を視覚化するカレンダーや達成率グラフにより、ユーザーのモチベーションを高い次元で維持する工夫を凝らしています。',
      category: 'Education',
      image: EitanImg,
      technologies: [
        'Swift',
        'SwiftUI',
        'SwiftData',
        'AVFoundation',
        'SwiftUI Charts',
        'StoreKit 2',
      ],
      highlights: [
        {
          title: '堅牢なデータ同期・更新メカニズム (Re-seeding)',
          content:
            'マスターデータ（JSON）とユーザーの学習記録（SwiftData）を分離。アプリ更新時にマスターデータのみを安全にマージし、ユーザーの正解数や苦手フラグを損なうことなく項目追加や修正を行える、拡張性の高い同期ロジックを独自実装。',
        },
        {
          title: 'クリーンアーキテクチャ (MVVM + Repository) の適用',
          content:
            'ビジネスロジックをUseCaseに集約し、データアクセスをRepositoryで抽象化。Viewとロジックを完全に分離することで、複雑な学習フローのユニットテストを容易にし、高い保守性を確保。',
        },
        {
          title: 'レスポンシブかつリッチなUIシステム',
          content:
            'iPhone/iPad両対応のGridレイアウトを採用。SwiftUIのMaterialやGradientを駆使したHeroセクションや、学習進捗を直感的に示すカスタムカレンダービューなど、ネイティブフレームワークの性能を最大限に引き出したUI。',
        },
        {
          title: 'ステートマシンによる学習フロー制御',
          content:
            'StudySessionManagerによる一元的な状態管理を行い、音声の終了検知から次のステップへの自動遷移までを非同期処理（Swift Concurrency）で制御。ユーザー操作を妨げないスムーズな学習体験を実現。',
        },
      ],
      links: [
        {
          label: 'View on App Store',
          url: 'https://apps.apple.com/app/id6755414131',
        },
      ],
    },
  ];

  const webApps = [
    {
      id: 7,
      platform: 'Web',
      title: 'budou-design.com',
      tagline: 'The Digital Hub of Creativity.',
      description: 'This site. Budou Design の公式ポートフォリオサイト。',
      overview: '当サイトの紹介。',
      category: 'Web',
      image: PortfolioImg,
      technologies: ['React', 'TypeScript', 'Tailwind CSS', 'GSAP', 'Firebase'],
      highlights: [
        {
          title: 'React & TypeScript による堅牢な設計',
          content:
            'コンポーネントベースの設計により、高い保守性と再利用性を実現。TypeScriptを採用することで、型安全な開発環境を構築し、バグの未然防止とスムーズな機能拡張を可能にしています。',
        },
        {
          title: 'Tailwind CSS を駆使したモダンUI',
          content:
            'ユーティリティファーストなCSS設計により、レスポンシブ対応や複雑なアニメーションを軽量かつ高速に実装。Glassmorphism（ガラス風デザイン）やグロー効果を多用し、リッチな視覚体験を提供。',
        },
        {
          title: 'GSAP による動的な演出',
          content:
            'スクロールに応じたヘッダーの透過率変化や要素のフェードなど、GSAPを活用した滑らかなインタラクションを配置。静的なWebサイトを超えた、心地よいユーザー体験を追求。',
        },
        {
          title: 'Firebase Hosting による高速配信',
          content:
            'Firebase Hosting を活用し、グローバルな配信速度と信頼性を確保。GitHub Actions による CI/CD を構築し、コードの変更を瞬時に本番環境へ反映させるモダンな開発フローを実現。',
        },
      ],
      links: [
        {
          label: 'Visit Website',
          url: 'https://budou-design.com',
        },
      ],
    },
  ];

  const designApps = [
    {
      id: 5,
      platform: 'iOS',
      title: 'iMessage Stickers',
      description:
        'Express yourself with unique and fun sticker packs for iMessage.',
      category: 'Design',
      image: IMessageImg,
      technologies: ['Adobe Fresco', 'Photoshop', 'iMessage Extension'],
      links: [
        {
          label: 'View on App Store',
          url: 'https://apps.apple.com/app/id6758271623',
        },
      ],
    },
    {
      id: 6,
      title: 'LINE Stickers',
      description: 'Original sticker sets available on the LINE Store.',
      category: 'Design',
      image: LineImg,
      technologies: ['Adobe Fresco', 'Photoshop', 'Character Design'],
      links: [
        {
          label: 'View on LINE Store',
          url: 'https://store.line.me/stickershop/author/547401/en',
        },
      ],
    },
  ];

  const AppCard = ({ app }: { app: any }) => (
    <div
      onClick={() => openModal(app)}
      className="relative bg-white rounded-2xl shadow-xl transition-transform hover:scale-105 border border-gray-100 flex flex-col h-full cursor-pointer group"
    >
      {/* iOS Badge */}
      {app.platform === 'iOS' && (
        <div className="absolute -top-3 -left-3 z-20 bg-[#251b3b] text-white text-[10px] font-black px-3 py-1 rounded-lg shadow-2xl border border-white/20 tracking-widest uppercase transform -rotate-12 group-hover:rotate-0 transition-transform duration-300">
          iOS
        </div>
      )}

      <div className="relative w-full pb-[75%] bg-gray-50 overflow-hidden rounded-t-2xl">
        <img
          src={app.image}
          alt={app.title}
          className="absolute inset-0 w-full h-full object-contain"
        />
      </div>
      <div className="p-6 flex flex-col flex-grow text-left">
        <span className="inline-block px-3 py-1 text-xs font-semibold text-[#6e1e50] bg-[#6e1e50]/10 rounded-full mb-3 self-start">
          {app.category}
        </span>
        <h3 className="text-2xl font-bold text-[#251b3b] mb-2">{app.title}</h3>
        <p className="text-[#37295a] mb-6 leading-relaxed flex-grow line-clamp-2">
          {app.description}
        </p>
        <div
          className="flex flex-col gap-3"
          onClick={(e) => e.stopPropagation()}
        >
          {app.links.map((link: any, index: number) => (
            <a
              key={index}
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full bg-[#6e1e50] text-white font-bold py-3 px-4 rounded-xl hover:bg-[#8b2b68] transition-colors shadow-md text-sm text-center"
            >
              {link.label}
            </a>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#fafafa] bg-gradient-to-b from-white via-[#fdf6f9] to-[#f7edf3] pt-20 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h1 className="text-4xl font-bold text-[#251b3b] sm:text-5xl">
            Portfolio
          </h1>
        </div>

        {/* Application Section */}
        <section className="mb-40">
          <h2 className="text-6xl sm:text-8xl font-thin text-[#251b3b]/80 mb-20 tracking-wide">
            Application
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {applicationApps.map((app) => (
              <AppCard key={app.id} app={app} />
            ))}
          </div>
        </section>

        {/* Web Section */}
        <section className="mb-40">
          <h2 className="text-6xl sm:text-8xl font-thin text-[#251b3b]/80 mb-20 tracking-wide">
            Web
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {webApps.map((app) => (
              <AppCard key={app.id} app={app} />
            ))}
          </div>
        </section>

        {/* Design Section */}
        <section className="mb-40">
          <h2 className="text-6xl sm:text-8xl font-thin text-[#251b3b]/80 mb-20 tracking-wide">
            Design & Stickers
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {designApps.map((app) => (
              <AppCard key={app.id} app={app} />
            ))}
          </div>
        </section>
      </div>

      {/* Detail Overlay with Animation */}
      {activeApp && (
        <div
          className={`fixed inset-0 z-[100] transition-all duration-500 ease-out overflow-y-auto bg-black/90 backdrop-blur-3xl ${
            isVisible ? 'opacity-100' : 'opacity-0'
          }`}
          onClick={closeModal}
        >
          {/* Background Glows */}
          <div className={`fixed inset-0 overflow-hidden pointer-events-none transition-opacity duration-1000 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
            <div className="absolute -top-[10%] -left-[10%] w-[40%] h-[40%] bg-[#6e1e50]/20 blur-[120px] rounded-full"></div>
            <div className="absolute -bottom-[10%] -right-[10%] w-[40%] h-[40%] bg-[#6e1e50]/20 blur-[120px] rounded-full"></div>
          </div>

          {/* Close Button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              closeModal();
            }}
            className={`fixed top-8 right-8 text-white/40 hover:text-white transition-all duration-500 p-4 z-[110] flex items-center justify-center bg-white/5 rounded-full backdrop-blur-md border border-white/10 ${
              isVisible ? 'translate-y-0 opacity-100' : '-translate-y-4 opacity-0'
            }`}
            aria-label="Close modal"
          >
            <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          <div className="min-h-full flex items-center justify-center p-4 sm:p-12 py-24">
            <div
              className={`max-w-5xl w-full bg-white/[0.03] backdrop-blur-3xl border border-white/10 rounded-[48px] p-8 sm:p-24 flex flex-col shadow-2xl relative overflow-hidden text-left transition-all duration-500 ease-out ${
                isVisible ? 'translate-y-0 scale-100 opacity-100' : 'translate-y-8 scale-95 opacity-0'
              }`}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Inner Glow */}
              <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-white/20 to-transparent"></div>
              
              {/* Top Section */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-20">
                <div>
                  <span className="inline-block px-4 py-1.5 bg-[#6e1e50]/20 text-[#c57ba8] rounded-full text-xs font-bold tracking-widest uppercase mb-6 border border-[#6e1e50]/30">
                    {activeApp.category}
                  </span>
                  <h2 className="text-3xl sm:text-5xl font-bold mb-6 tracking-tight leading-tight text-white">
                    {activeApp.title}
                  </h2>
                  {activeApp.tagline && (
                    <p className="text-xl sm:text-2xl text-white/40 font-light tracking-wide italic">
                      {activeApp.tagline}
                    </p>
                  )}
                </div>
                
                <div className="flex justify-center">
                  <div className="relative w-full max-w-sm aspect-[4/3] group">
                    <div className="absolute inset-0 bg-[#6e1e50]/30 blur-[80px] rounded-full opacity-40 group-hover:opacity-70 transition-opacity duration-700"></div>
                    <img
                      src={activeApp.image}
                      alt={activeApp.title}
                      className="relative w-full h-full object-contain drop-shadow-[0_32px_64px_rgba(0,0,0,0.6)]"
                    />
                  </div>
                </div>
              </div>

              {/* Main Content */}
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 border-t border-white/5 pt-16">
                <div className="lg:col-span-5">
                  <h3 className="text-xs font-black uppercase tracking-[0.3em] text-[#6e1e50] mb-8">
                    Overview
                  </h3>
                  <p className="text-xl text-white/70 leading-relaxed font-light mb-12">
                    {activeApp.overview || activeApp.description}
                  </p>
                  
                  <h3 className="text-xs font-black uppercase tracking-[0.3em] text-[#6e1e50] mb-8">
                    Technologies
                  </h3>
                  <div className="flex flex-wrap gap-2 mb-12">
                    {activeApp.technologies.map((tech: string) => (
                      <span
                        key={tech}
                        className="px-4 py-2 bg-white/5 border border-white/10 rounded-xl text-xs font-medium text-white/50 hover:text-white hover:bg-white/10 transition-colors"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>

                  <div className="flex flex-col gap-4 mt-auto">
                    {activeApp.links.map((link: any, index: number) => (
                      <a
                        key={index}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group flex items-center justify-between bg-white text-[#6e1e50] font-bold py-5 px-8 rounded-[24px] hover:bg-[#6e1e50] hover:text-white transition-all duration-500 shadow-xl"
                      >
                        <span className="text-lg">{link.label}</span>
                        <svg className="w-6 h-6 transform group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                        </svg>
                      </a>
                    ))}
                  </div>
                </div>

                {activeApp.highlights && (
                  <div className="lg:col-span-7 lg:pl-16 lg:border-l border-white/5">
                    <h3 className="text-xs font-black uppercase tracking-[0.3em] text-[#6e1e50] mb-8">
                      Technical Highlights
                    </h3>
                    <div className="space-y-12">
                      {activeApp.highlights.map(
                        (item: any, index: number) => (
                          <div key={index} className="relative group">
                            <h4 className="text-xl font-bold text-white mb-4 group-hover:text-[#6e1e50] transition-colors duration-300">
                              {item.title}
                            </h4>
                            <p className="text-white/50 leading-relaxed text-base font-light">
                              {item.content}
                            </p>
                          </div>
                        )
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Portfolio;
